import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import FilterListIcon from '@mui/icons-material/FilterList';
import { alpha } from '@mui/material/styles';
import {
    chatColors,
    chatMenuItemSx,
    chatMenuLabelSx,
    chatPopoverPaperSx,
    chatRadii
} from './chatTheme';

export const FILTER_ALL = 'all';
export const FILTER_ROOM = 'room';
export const FILTER_QUESTION = 'question';

// Namespaced so a slide's itemId can never collide with the fixed keys above.
export const slideFilterKey = (slideItemId) => `slide:${slideItemId}`;

const tagsOf = (chat) => (chat && chat.slideTags) || [];

/**
 * Whether one top-level chat belongs under the active filter. "Room" means
 * untagged in every sense: no slide and not a Question.
 */
export function matchesFilter(chat, filter) {
    if (!filter || filter === FILTER_ALL) {
        return true;
    }
    if (filter === FILTER_ROOM) {
        return tagsOf(chat).length === 0 && !chat.question;
    }
    if (filter === FILTER_QUESTION) {
        return !!chat.question;
    }
    return tagsOf(chat).some((tag) => slideFilterKey(tag.slideItemId) === filter);
}

/**
 * The filter rows and their counts.
 *
 * Slide rows are the room's live slides plus any slide still carried by a
 * loaded chat's tags - that second source is what keeps a deleted slide
 * filterable while chats point at it, and drops its row once none do.
 */
export function buildFilterOptions(chats = [], slides = []) {
    const slideRows = new Map();
    slides.forEach((slide) => {
        if (!slide || slide.itemId == null) {
            return;
        }
        slideRows.set(slide.itemId, { number: Number(slide.index), deleted: false, live: true });
    });
    chats.forEach((chat) =>
        tagsOf(chat).forEach((tag) => {
            if (slideRows.has(tag.slideItemId)) {
                return;
            }
            slideRows.set(tag.slideItemId, {
                number: Number(tag.slideNumber),
                deleted: !!tag.deleted,
                live: false
            });
        })
    );

    const countOf = (filter) => chats.filter((chat) => matchesFilter(chat, filter)).length;

    return [
        { key: FILTER_ALL, label: 'All', count: chats.length, deleted: false },
        { key: FILTER_ROOM, label: 'Room', count: countOf(FILTER_ROOM), deleted: false }
    ]
        .concat(
            Array.from(slideRows.entries())
                .map(([itemId, row]) => ({
                    key: slideFilterKey(itemId),
                    label: `Slide ${row.number}`,
                    count: countOf(slideFilterKey(itemId)),
                    deleted: row.deleted,
                    live: row.live,
                    number: row.number
                }))
                .filter((row) => row.live || row.count > 0)
                .sort((a, b) => a.number - b.number)
        )
        .concat([
            {
                key: FILTER_QUESTION,
                label: 'Question',
                count: countOf(FILTER_QUESTION),
                deleted: false
            }
        ]);
}

/**
 * Whether the active filter still has a row to sit on.
 *
 * All, Room and Question are always listed. A slide row is not: it comes from
 * the live slides or from a loaded chat's tags, so picking a live slide nothing
 * is tagged to and then deleting that slide leaves the filter pointing at a row
 * that no longer exists - the button falls back to reading "All" while the list
 * goes on hiding every message.
 */
export function filterHasRow(filter, chats = [], slides = []) {
    if (filter === FILTER_ALL || filter === FILTER_ROOM || filter === FILTER_QUESTION) {
        return true;
    }
    // The same two sources buildFilterOptions draws slide rows from.
    return (
        slides.some((slide) => slide && slideFilterKey(slide.itemId) === filter) ||
        chats.some((chat) => matchesFilter(chat, filter))
    );
}

/**
 * The filter button at the left of the composer bar, plus its menu. Single
 * select; the button tints primary whenever the filter is anything but "All".
 */
export default function ({
    filter,
    chats = [],
    slides = [],
    open,
    anchorEl,
    onOpen,
    onClose,
    onChange
}) {
    const options = buildFilterOptions(chats, slides);
    const active = options.find((option) => option.key === filter);
    const isFiltered = filter !== FILTER_ALL;

    return (
        <>
            <ButtonBase
                aria-label={active ? `Filter chats, showing ${active.label}` : 'Filter chats'}
                title={active ? active.label : 'All'}
                aria-haspopup='true'
                onClick={onOpen}
                sx={(theme) => ({
                    // Sized to its label and never shrunk - see ChatScopeMenu.
                    flex: '0 0 auto',
                    minWidth: 44,
                    maxWidth: 148,
                    height: 48,
                    gap: 0.5,
                    padding: '0 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: chatRadii.input,
                    border: `1px solid ${
                        isFiltered
                            ? alpha(chatColors(theme).primary, 0.4)
                            : chatColors(theme).border
                    }`,
                    color: isFiltered ? chatColors(theme).primary : chatColors(theme).text,
                    backgroundColor: isFiltered ? chatColors(theme).primaryTint : 'transparent',
                    '&:hover': { backgroundColor: chatColors(theme).hoverSurface }
                })}>
                <FilterListIcon sx={{ flex: '0 0 auto', fontSize: 18 }} />
                {/* Icon-only on a narrow mobile tab: the filter and scope
                    buttons plus the send button would otherwise leave the text
                    input around 50px wide. The tint still shows a filter is on,
                    and the title/aria-label still name it. */}
                <Box
                    component='span'
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                    {active ? active.label : 'All'}
                </Box>
            </ButtonBase>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: chatPopoverPaperSx }}>
                <Typography sx={chatMenuLabelSx}>SHOW CHATS FROM</Typography>
                {options.map((option) => (
                    <ButtonBase
                        key={option.key}
                        onClick={() => {
                            onChange(option.key);
                            onClose();
                        }}
                        sx={(theme) => ({
                            ...chatMenuItemSx(theme),
                            justifyContent: 'space-between',
                            color:
                                option.key === filter
                                    ? chatColors(theme).primary
                                    : chatColors(theme).text,
                            backgroundColor:
                                option.key === filter
                                    ? chatColors(theme).primaryTint
                                    : 'transparent'
                        })}>
                        <Box
                            component='span'
                            sx={(theme) => ({
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                // A deleted slide reads inert here too.
                                color: option.deleted ? chatColors(theme).deletedChip : 'inherit'
                            })}>
                            {option.label}
                        </Box>
                        <Box
                            component='span'
                            sx={{ flex: '0 0 auto', fontSize: 12, color: 'text.secondary' }}>
                            {option.count}
                        </Box>
                    </ButtonBase>
                ))}
            </Popover>
        </>
    );
}
